/**
 * Rules learned from corrections of AI ticket drafts (API 0422).
 *
 * The API proposes a rule once the same corrected value has been seen on three
 * distinct drafts for one customer + line item type + field. An admin
 * activates or dismisses it; only an active rule reaches a prompt. This module
 * reads the GET /ai-rules payload (camelCase) and renders a correction's
 * before → after. It decides nothing the API has already decided: the rule's
 * wording is the API's `ruleText`, and the count is the API's
 * `correctionCount`.
 *
 * NOT ON THE PAYLOAD: the rate sheet price for a rate rule's line item. The API
 * returns no such figure, so the page shows none — computing it here from the
 * rate sheet endpoints would be a second answer to a question the API owns.
 */
import { fmtCurrency } from './display-labels';
import { formatInstantDay } from './calendar-date';
import type { BillingFlagType } from '../types/billing-flags';

export type RuleStatus = 'proposed' | 'active' | 'dismissed';
/** A billing_preflight rule's field is a flag type (api#916), not a corrected line field. */
export type CorrectionField = 'rate' | 'quantity' | 'description' | 'line_added' | 'line_removed' | BillingFlagType;

export interface RuleExample {
  id: string;
  ticketNumber: string | null;
  rigName: string | null;
  before: unknown;
  after: unknown;
  correctedByName: string | null;
  createdAt: string | null;
}

export interface AiRule {
  id: string;
  /** ticket_prefill, pay_suggestion or billing_preflight — as sent, never defaulted. */
  kind: string | null;
  status: RuleStatus | string;
  field: CorrectionField | string;
  lineItemType: string;
  ruleText: string;
  /** Null when the API sent none — never defaulted to 0. */
  correctionCount: number | null;
  examples: RuleExample[];
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/**
 * The rule list, or a thrown error when the payload is not a list — a shape we
 * cannot read is a failed load, not "no rules".
 */
export function parseAiRules(payload: unknown): AiRule[] {
  if (!Array.isArray(payload)) throw new Error('GET /ai-rules did not return a list');
  return payload.map((r: any) => {
    const rawCount = r.correctionCount ?? r.correction_count;
    return {
      id: String(r.id),
      kind: str(r.kind),
      status: r.status,
      field: r.field,
      lineItemType: r.lineItemType ?? r.line_item_type,
      ruleText: str(r.ruleText ?? r.rule_text) ?? '—',
      correctionCount: rawCount === null || rawCount === undefined || rawCount === '' || !Number.isFinite(Number(rawCount))
        ? null
        : Number(rawCount),
      examples: Array.isArray(r.examples)
        ? r.examples.map((e: any) => ({
            id: String(e.id),
            ticketNumber: str(e.ticketNumber ?? e.ticket_number),
            rigName: str(e.rigName ?? e.rig_name),
            before: e.before,
            after: e.after,
            correctedByName: str(e.correctedByName ?? e.corrected_by_name),
            createdAt: str(e.createdAt ?? e.created_at),
          }))
        : [],
    };
  });
}

/** A line snapshot ({description, rate, quantity}) as one short phrase. */
function linePhrase(v: unknown): string {
  if (!v || typeof v !== 'object') return '—';
  const l = v as { description?: unknown; rate?: unknown; quantity?: unknown };
  const desc = str(l.description) ?? '—';
  const qty = l.quantity === null || l.quantity === undefined || l.quantity === '' ? '—' : String(Number(l.quantity));
  return `${desc} (${qty} × ${fmtCurrency(l.rate as any)})`;
}

function valuePhrase(field: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  switch (field) {
    case 'rate': return fmtCurrency(v as any);
    case 'quantity': return Number.isFinite(Number(v)) ? String(Number(v)) : '—';
    case 'description': return typeof v === 'string' ? `"${v}"` : '—';
    default: return '—';
  }
}

/**
 * A correction as before → after. An added line had no "before" and a removed
 * line has no "after"; both sides are named for what they are rather than
 * printed as an em dash that would read as a value that failed to arrive.
 */
export function describeChange(field: string, before: unknown, after: unknown): { before: string; after: string } {
  if (field === 'line_added') return { before: 'No line', after: linePhrase(after) };
  if (field === 'line_removed') return { before: linePhrase(before), after: 'Removed' };
  return { before: valuePhrase(field, before), after: valuePhrase(field, after) };
}

/** The day a correction was made, in the TENANT's zone, or '—'. `createdAt` is an instant. */
export function exampleDate(createdAt: string | null, timeZone: string | null): string {
  return formatInstantDay(createdAt, timeZone) ?? '—';
}
